import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import MainNavigation from '../../components/ui/MainNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import QuoteModal from './components/QuoteModal';

const QuoteManagementSystem = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [showModal, setShowModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  useEffect(() => {
    if (!authLoading && (!user || !['provider', 'supplier', 'sales_agent', 'reseller', 'superadmin']?.includes(userProfile?.user_type))) {
      navigate('/provider-login');
    }
  }, [user, userProfile, authLoading, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadQuotes();
    }
  }, [user]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [quotes, searchTerm, filterStatus, filterType, sortBy]);

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('provider_quotes')?.select('*')?.eq('provider_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...quotes];

    // Search filter
    if (searchTerm) {
      filtered = filtered?.filter(q => 
        q?.customer_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        q?.quote_number?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        q?.customer_email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered?.filter(q => q?.status === filterStatus);
    }

    // Type filter
    if (filterType !== 'all') {
      filtered = filtered?.filter(q => q?.project_type === filterType);
    }

    // Sort
    switch (sortBy) {
      case 'date_desc':
        filtered?.sort((a, b) => new Date(b?.created_at) - new Date(a?.created_at));
        break;
      case 'date_asc':
        filtered?.sort((a, b) => new Date(a?.created_at) - new Date(b?.created_at));
        break;
      case 'amount_desc':
        filtered?.sort((a, b) => parseFloat(b?.quote_amount || 0) - parseFloat(a?.quote_amount || 0));
        break;
      case 'amount_asc':
        filtered?.sort((a, b) => parseFloat(a?.quote_amount || 0) - parseFloat(b?.quote_amount || 0));
        break;
      case 'customer_asc':
        filtered?.sort((a, b) => a?.customer_name?.localeCompare(b?.customer_name));
        break;
      default:
        break;
    }

    setFilteredQuotes(filtered);
  };

  const handleCreateQuote = () => {
    setSelectedQuote(null);
    setShowModal(true);
  };

  const handleEditQuote = (quote) => {
    setSelectedQuote(quote);
    setShowModal(true);
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!confirm('Weet je zeker dat je deze offerte wilt verwijderen?')) return;

    try {
      const { error } = await supabase?.from('provider_quotes')?.delete()?.eq('id', quoteId)?.eq('provider_id', user?.id);

      if (error) throw error;
      loadQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      alert('Fout bij verwijderen van offerte');
    }
  };

  const handleDuplicateQuote = async (quote) => {
    try {
      const newQuote = {
        provider_id: user?.id,
        customer_id: quote?.customer_id,
        quote_number: `${quote?.quote_number}-COPY`,
        customer_name: quote?.customer_name,
        customer_email: quote?.customer_email,
        customer_phone: quote?.customer_phone,
        project_type: quote?.project_type,
        quote_amount: quote?.quote_amount,
        status: 'concept',
        quote_date: new Date()?.toISOString()?.split('T')?.[0],
        description: quote?.description,
      };

      const { error } = await supabase?.from('provider_quotes')?.insert([newQuote]);

      if (error) throw error;
      loadQuotes();
    } catch (error) {
      console.error('Error duplicating quote:', error);
      alert('Fout bij dupliceren van offerte');
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      const { error } = await supabase?.from('provider_quotes')?.update({ status: newStatus })?.eq('id', quoteId)?.eq('provider_id', user?.id);

      if (error) throw error;
      loadQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Fout bij wijzigen van status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      concept: 'bg-muted text-muted-foreground border-border',
      verstuurd: 'bg-info/10 text-info border-info/20',
      in_overweging: 'bg-warning/10 text-warning border-warning/20',
      gewonnen: 'bg-success/10 text-success border-success/20',
      verloren: 'bg-error/10 text-error border-error/20',
    };
    return styles?.[status] || styles?.concept;
  };

  const getStatusLabel = (status) => {
    const labels = {
      concept: 'Concept',
      verstuurd: 'Verstuurd',
      in_overweging: 'In Overweging',
      gewonnen: 'Gewonnen',
      verloren: 'Verloren',
    };
    return labels?.[status] || status;
  };

  const statusOptions = [
    { value: 'all', label: 'Alle Statussen' },
    { value: 'concept', label: 'Concept' },
    { value: 'verstuurd', label: 'Verstuurd' },
    { value: 'in_overweging', label: 'In Overweging' },
    { value: 'gewonnen', label: 'Gewonnen' },
    { value: 'verloren', label: 'Verloren' },
  ];

  const typeOptions = [
    { value: 'all', label: 'Alle Types' },
    { value: 'zonnepanelen', label: 'Zonnepanelen' },
    { value: 'isolatie', label: 'Isolatie' },
    { value: 'warmtepomp', label: 'Warmtepomp' },
    { value: 'ventilatie', label: 'Ventilatie' },
    { value: 'thuisbatterij', label: 'Thuisbatterij' },
    { value: 'waterontharder', label: 'Waterontharder' },
    { value: 'overig', label: 'Overig' },
  ];

  const sortOptions = [
    { value: 'date_desc', label: 'Datum (nieuwste eerst)' },
    { value: 'date_asc', label: 'Datum (oudste eerst)' },
    { value: 'amount_desc', label: 'Bedrag (hoogste eerst)' },
    { value: 'amount_asc', label: 'Bedrag (laagste eerst)' },
    { value: 'customer_asc', label: 'Klant (A-Z)' },
  ];

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Offertes laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
        {/* Header */}
        <div className="bg-card border-b border-border shadow-elevation-1 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Offertebeheer</h1>
              <p className="text-sm text-muted-foreground mt-1">{filteredQuotes?.length} offertes gevonden</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" iconName="Download" onClick={() => alert('Export functionaliteit komt binnenkort')}>Exporteren</Button>
              <Button iconName="Plus" onClick={handleCreateQuote}>Nieuwe Offerte</Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 bg-card border-b border-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              placeholder="Zoek op klant, nummer, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
            <Select
              options={statusOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Filter op status"
            />
            <Select
              options={typeOptions}
              value={filterType}
              onChange={setFilterType}
              placeholder="Filter op type"
            />
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sorteer op"
            />
          </div>
        </div>

        {/* Quotes Table */}
        <div className="p-6">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Offertenummer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Klant</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Bedrag</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredQuotes?.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                        Geen offertes gevonden
                      </td>
                    </tr>
                  ) : (
                    filteredQuotes?.map((quote) => (
                      <tr key={quote?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-4 text-sm font-medium text-foreground">{quote?.quote_number}</td>
                        <td className="px-4 py-4">
                          <div>
                            <p className="text-sm font-medium text-foreground">{quote?.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{quote?.customer_email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-foreground capitalize">{quote?.project_type}</td>
                        <td className="px-4 py-4 text-sm font-medium text-foreground">€{parseFloat(quote?.quote_amount || 0)?.toLocaleString('nl-NL')}</td>
                        <td className="px-4 py-4">
                          <Select
                            options={statusOptions?.filter(opt => opt?.value !== 'all')}
                            value={quote?.status}
                            onChange={(newStatus) => handleStatusChange(quote?.id, newStatus)}
                            className="w-40"
                          />
                        </td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">
                          {new Date(quote?.quote_date)?.toLocaleDateString('nl-NL')}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-2">
                            <Button variant="ghost" size="icon" onClick={() => handleEditQuote(quote)} title="Bewerken">
                              <Icon name="Edit" size={16} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDuplicateQuote(quote)} title="Dupliceren">
                              <Icon name="Copy" size={16} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteQuote(quote?.id)} title="Verwijderen">
                              <Icon name="Trash2" size={16} className="text-error" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Quote Modal */}
      {showModal && (
        <QuoteModal
          quote={selectedQuote}
          onClose={() => setShowModal(false)}
          onSave={() => {
            setShowModal(false);
            loadQuotes();
          }}
        />
      )}
    </div>
  );
};

export default QuoteManagementSystem;